import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  NgZone,
  ElementRef,
  ViewChild,
  AfterViewChecked
} from '@angular/core';
import { Subscription, take } from 'rxjs';
import { Banner } from '@shared/entities/banner';
import { BannerService } from '@shared/services/api/banner.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { Roles } from '@shared/models/types';
import { format } from 'date-fns';

export interface BannerWithImage extends Banner {
  imageUrl?: string;
}

declare let bootstrap: any;

@Component({
  selector: 'app-banner-carousel',
  templateUrl: './banner-carousel.component.html',
  styleUrl: './banner-carousel.component.scss'
})
export class BannerCarouselComponent implements OnInit, OnDestroy, AfterViewChecked {
  @ViewChild('carouselElement', { static: false }) carouselElement!: ElementRef;

  banners: BannerWithImage[] = [];
  isMobile = false;
  loading = true;
  error = false;

  private carouselInstance: any;
  private objectUrls: string[] = [];
  private subscriptions: Subscription[] = [];
  private carouselInitialized = false;
  private autoSlideInterval: any;

  constructor(
    private _bannerService: BannerService,
    private _profileService: ProfileService,
    private _fsAttachmentService: FsAttachmentService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private el: ElementRef
  ) {
    this.checkScreenSize();
  }

  ngOnInit() {
    this.loadBanners();
    window.addEventListener('resize', this.onResize.bind(this));
  }

  ngAfterViewChecked() {
    if (!this.carouselInitialized && this.banners.length > 0 && !this.loading) {
      this.initializeCarousel();
      this.carouselInitialized = true;
    }
  }

  manualNext(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.directManipulateCarousel('next');
    this.resetAutoSlideTimer();
    return false;
  }

  manualPrev(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.directManipulateCarousel('prev');
    this.resetAutoSlideTimer();
    return false;
  }

  adjustCarouselHeight(): void {
    if (this.carouselElement && this.carouselElement.nativeElement) {
      const carouselInner = this.carouselElement.nativeElement.querySelector('.carousel-inner');
      if (carouselInner) {
        setTimeout(() => this.cdr.detectChanges(), 0);
      }
    }
  }

  private resetAutoSlideTimer() {
    if (this.banners.length <= 1) return;

    if (this.carouselInstance) {
      try {
        this.carouselInstance.pause();

        setTimeout(() => {
          this.carouselInstance.cycle();
        }, 10);
      } catch {
        // Ignore
      }
    }

    this.setupFallbackAutoSlide();
  }

  private directManipulateCarousel(direction: 'next' | 'prev') {
    if (this.banners.length <= 1) return;

    try {
      if (this.carouselInstance) {
        if (direction === 'next') {
          this.carouselInstance.next();
        } else {
          this.carouselInstance.prev();
        }
        return;
      }

      const carousel = document.getElementById('bannerCarousel');
      if (!carousel) return;

      const items = carousel.querySelectorAll('.carousel-item');
      if (!items.length) return;

      let activeIndex = -1;
      items.forEach((item, index) => {
        if (item.classList.contains('active')) {
          activeIndex = index;
        }
      });

      if (activeIndex === -1) return;

      const newIndex =
        direction === 'next' ? (activeIndex + 1) % items.length : (activeIndex - 1 + items.length) % items.length;

      if (direction === 'next') {
        items[activeIndex].classList.add('active', 'carousel-item-start');
        items[newIndex].classList.add('carousel-item-next');

        setTimeout(() => {
          items[newIndex].classList.add('carousel-item-start');
          setTimeout(() => {
            items[activeIndex].classList.remove('active', 'carousel-item-start');
            items[newIndex].classList.remove('carousel-item-next', 'carousel-item-start');
            items[newIndex].classList.add('active');
          }, 600);
        }, 10);
      } else {
        items[activeIndex].classList.add('active', 'carousel-item-end');
        items[newIndex].classList.add('carousel-item-prev');

        setTimeout(() => {
          items[newIndex].classList.add('carousel-item-end');
          setTimeout(() => {
            items[activeIndex].classList.remove('active', 'carousel-item-end');
            items[newIndex].classList.remove('carousel-item-prev', 'carousel-item-end');
            items[newIndex].classList.add('active');
          }, 600);
        }, 10);
      }

      const indicators = carousel.querySelectorAll('.carousel-indicators button');
      if (indicators.length) {
        indicators[activeIndex].classList.remove('active');
        indicators[activeIndex].removeAttribute('aria-current');
        indicators[newIndex].classList.add('active');
        indicators[newIndex].setAttribute('aria-current', 'true');
      }
    } catch {
      // Ignore
    }
  }

  private initializeCarousel() {
    try {
      const carouselElement = this.el.nativeElement.querySelector('#bannerCarousel');
      if (!carouselElement) {
        return;
      }

      if (this.banners.length <= 1) {
        return;
      }

      if (typeof bootstrap === 'undefined') {
        this.setupFallbackAutoSlide();
        return;
      }

      carouselElement.setAttribute('data-bs-ride', 'carousel');
      carouselElement.setAttribute('data-bs-interval', '7000');
      carouselElement.setAttribute('data-bs-slide', 'true');

      setTimeout(() => {
        try {
          if (this.carouselInstance) {
            this.carouselInstance.dispose();
          }

          this.carouselInstance = new bootstrap.Carousel(carouselElement, {
            interval: 12000,
            keyboard: true,
            pause: false,
            wrap: true,
            touch: true,
            ride: 'carousel',
            animation: true,
            slide: true
          });

          const carouselInner = carouselElement.querySelector('.carousel-inner');
          if (carouselInner && this.banners.length > 0) {
            const firstImage = carouselInner.querySelector('img');
            if (firstImage) {
              firstImage.addEventListener('load', () => {
                carouselElement.style.display = 'none';
                void carouselElement.offsetHeight;
                carouselElement.style.display = '';

                this.carouselInstance.cycle();
              });
            } else {
              this.carouselInstance.cycle();
            }
          } else {
            this.carouselInstance.cycle();
          }

          const prevButton = carouselElement.querySelector('.carousel-control-prev');
          const nextButton = carouselElement.querySelector('.carousel-control-next');

          if (prevButton) {
            prevButton.addEventListener('click', (e: Event) => {
              e.preventDefault();
              if (this.carouselInstance) {
                this.carouselInstance.prev();
                this.resetAutoSlideTimer();
              }
            });
          }

          if (nextButton) {
            nextButton.addEventListener('click', (e: Event) => {
              e.preventDefault();
              if (this.carouselInstance) {
                this.carouselInstance.next();
                this.resetAutoSlideTimer();
              }
            });
          }
        } catch {
          this.setupFallbackAutoSlide();
        }
      }, 500);
    } catch {
      this.setupFallbackAutoSlide();
    }
  }

  private setupFallbackAutoSlide() {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }

    if (this.banners.length <= 1) {
      return;
    }

    this.autoSlideInterval = setInterval(() => {
      this.zone.run(() => {
        this.directManipulateCarousel('next');
      });
    }, 7000);
  }

  private loadBanners() {
    this.loading = true;
    this.error = false;
    this.banners = [];
    this.cdr.detectChanges();

    const sub = this._profileService.role$.pipe(take(1)).subscribe({
      next: (userRole) => {
        const currentDate = new Date();
        const formattedDate = format(currentDate, 'yyyy-MM-dd');

        const bannerSub = this._bannerService
          .getHomepageBanner({
            role: userRole !== Roles.ADMIN ? userRole : undefined,
            date: formattedDate
          })
          .pipe(take(1))
          .subscribe({
            next: (banners) => {
              if (!banners || banners.length === 0) {
                this.loading = false;
                this.banners = [];
                this.cdr.detectChanges();
                return;
              }

              const bannersWithImages: BannerWithImage[] = [];
              let completedCount = 0;

              banners.forEach((banner) => {
                const attachmentId =
                  this.isMobile && banner.mobileAttachmentId ? banner.mobileAttachmentId : banner.desktopAttachmentId;

                if (!attachmentId) {
                  bannersWithImages.push({ ...banner, imageUrl: '' });
                  checkCompletion();
                  return;
                }

                const attachmentSub = this._fsAttachmentService
                  .getAttachmentUrl(attachmentId)
                  .pipe(take(1))
                  .subscribe({
                    next: (blob) => {
                      const url = URL.createObjectURL(blob);
                      this.objectUrls.push(url);
                      bannersWithImages.push({ ...banner, imageUrl: url });
                      checkCompletion();
                    },
                    error: () => {
                      bannersWithImages.push({ ...banner, imageUrl: '' });
                      checkCompletion();
                    }
                  });
                this.subscriptions.push(attachmentSub);
              });

              const checkCompletion = () => {
                completedCount++;

                if (completedCount === banners.length) {
                  this.zone.run(() => {
                    this.banners = [...bannersWithImages];
                    this.loading = false;
                    this.cdr.detectChanges();
                    this.carouselInitialized = false;
                  });
                }
              };
            },
            error: () => {
              this.zone.run(() => {
                this.error = true;
                this.loading = false;
                this.cdr.detectChanges();
              });
            }
          });
        this.subscriptions.push(bannerSub);
      },
      error: () => {
        this.zone.run(() => {
          this.error = true;
          this.loading = false;
          this.cdr.detectChanges();
        });
      }
    });
    this.subscriptions.push(sub);
  }

  private onResize() {
    const oldIsMobile = this.isMobile;
    this.checkScreenSize();

    if (oldIsMobile !== this.isMobile) {
      this.loadBanners();
    }
  }

  private checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (sub) {
        sub.unsubscribe();
      }
    });

    if (this.carouselInstance) {
      try {
        this.carouselInstance.dispose();
      } catch {
        // Ignore
      }
    }

    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }

    this.objectUrls.forEach((url) => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    });

    window.removeEventListener('resize', this.onResize.bind(this));
  }

  getProperUrl(url: string): string {
    if (!url) return '';

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return `https://${url}`;
  }
}
